const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

const mobileBreakpoint = 750;
const tabletBreakpoint = 990;

let header = document.querySelector('#shopify-section-header header');

window.addEventListener('shopify:section:load', (e) => {
  header = document.querySelector('#shopify-section-header header');
});

['load', 'scroll', 'resize'].forEach((eventName) => {
  window.addEventListener(eventName, (e) => {
    document.documentElement.style.setProperty(
      '--viewport-height',
      `${window.innerHeight}px`,
    );
  });
});

const onKeyUpEscape = (event) => {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
};

const getFocusableElements = (container) => {
  const selectors = [
    'summary',
    'a[href]',
    'button:enabled',
    '[tabindex]:not([tabindex^="-"])',
    '[draggable]',
    'area',
    'input:not([type=hidden]):enabled',
    'select:enabled',
    'textarea:enabled',
    'object',
    'iframe',
    'video',
  ];
  return Array.from(container.querySelectorAll(selectors.join(',')));
};

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute(
    'aria-expanded',
    summary.parentNode.hasAttribute('open'),
  );

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute(
      'aria-expanded',
      !event.currentTarget.closest('details').hasAttribute('open'),
    );
  });

  if (summary.closest('header-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

const removeTrapFocus = (elementToFocus = null) => {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
};

const trapFocus = (container, elementToFocus = container) => {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();
};

const serializeForm = (form) => {
  const obj = {};
  const formData = new FormData(form);
  for (const key of formData.keys()) {
    obj[key] = formData.get(key);
  }
  return JSON.stringify(obj);
};

const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

const handleize = (str) => str.replace(/[ /_]/g, '-').toLowerCase();

const decode = (str) => decodeURIComponent(str).replace(/\+/g, ' ');

const getOffsetTop = (element) => {
  let offsetTop = 0;

  do {
    if (!isNaN(element.offsetTop)) {
      offsetTop += element.offsetTop;
    }
  } while ((element = element.offsetParent));

  return offsetTop;
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this)),
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute('open');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling);
      summaryElement.nextElementSibling.removeEventListener(
        'transitionend',
        addTrapFocus,
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(summaryElement)
        : this.openMenuDrawer(summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        summaryElement.nextElementSibling.addEventListener(
          'transitionend',
          addTrapFocus,
        );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.style.overflow = 'hidden';
  }

  closeMenuDrawer(elementToFocus = false) {
    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle
      .querySelectorAll('.submenu-open')
      .forEach((submenu) => {
        submenu.classList.remove('submenu-open');
      });
    document.body.style.overflow = '';
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute('open') &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement
      .querySelector('summary')
      .setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(
            detailsElement.closest('details[open]'),
            detailsElement.querySelector('summary'),
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();

    this.isOpen = false;
    this.header =
      this.header || document.querySelector('#shopify-section-header header');
    this.debouncedResize = debounce(() => {
      const matchesBreakpoint = window.matchMedia(
        `(min-width: ${this.getAttribute('data-breakpoint')}px)`,
      ).matches;

      if (!matchesBreakpoint || !this.isOpen) return;

      this.closeMenuDrawer();
    }, 300);
    window.addEventListener('resize', this.debouncedResize.bind(this));
  }

  openMenuDrawer(summaryElement) {
    this.isOpen = true;
    this.header.classList.add('menu-open');
    super.openMenuDrawer(summaryElement);
  }

  closeMenuDrawer(elementToFocus) {
    this.isOpen = false;
    super.closeMenuDrawer(elementToFocus);
    this.header.classList.remove('is-hidden');
    this.header.classList.remove('menu-open');
  }
}

customElements.define('header-drawer', HeaderDrawer);

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
      '*',
    );
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

const fetchConfig = (type = 'json') => {
  return {
    method: 'POST',
    headers: {
      'Content-Type': `application/${type}`,
      'Accept': `application/${type}`,
    },
  };
};

class QuantityInput extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this)),
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    const previousValue = this.input.value;

    event.target.name === 'increment'
      ? this.input.stepUp()
      : this.input.stepDown();

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);
  }
}

customElements.define('quantity-input', QuantityInput);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;

    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));

      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.dialogHolder = this.querySelector('[role="dialog"]');
    this.querySelectorAll('[id^="ModalClose-"]').forEach((closer) => {
      closer.addEventListener('click', this.hide.bind(this, false));
    });
    this.addEventListener('keyup', (event) => {
      if (event.code?.toUpperCase() === 'ESCAPE') this.hide();
    });
    this.addEventListener('click', (event) => {
      if (event.target === this) this.hide();
    });
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    document.body.style.overflow = 'hidden';
    this.setAttribute('open', '');
    trapFocus(this, this.dialogHolder);
    window.pauseAllMedia();
  }

  hide() {
    document.body.style.overflow = '';
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalPopup extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.btnsToggle = Array.from(
      this.querySelectorAll('.js-btn-toggle'),
    ).filter((btn) => {
      return btn.closest('.js-modal') === this;
    });
    this.btnMain = this.btnsToggle[0];
    this.detailsElement = this.btnMain.parentElement;
    this.modalContent = this.querySelector('.js-modal-content');
    this.slider = this.closest('.swiper');
    this.focusableElements = getFocusableElements(this.modalContent);
    this.firstFocusableElement = this.focusableElements[0];
    this.isNested = false;

    this.detailsElement.setAttribute('open', '');

    this.setA11yAttr();

    this.addEventListener('keydown', (e) => {
      const isEscapeKey = e.key === 'Escape';
      if (!isEscapeKey || !this.isOpen) return;

      this.toggleModal();
    });

    this.btnsToggle.forEach((btnToggle) => {
      btnToggle.addEventListener('click', (e) => {
        e.preventDefault();

        this.toggleModal();
      });
    });

    window.addEventListener('resize', () => {
      const modalStyles = getComputedStyle(this);
      const isModalHidden = modalStyles.display === 'none';

      if (!isModalHidden || !this.isOpen) return;

      this.toggleModal();
    });
  }

  toggleModal() {
    if (this.closest('.swiper.swiper-initialized') != null) {
      this.closest('.swiper.swiper-initialized').classList.toggle('is-open');
    }

    this.isOpen = this.classList.toggle('is-open');

    this.btnsToggle.forEach((btnToggle) => {
      btnToggle.setAttribute('aria-expanded', this.isOpen);
    });

    if (this.isOpen) {
      if (this.slider) {
        this.slider.swiper.wrapperEl.style.left = `${this.slider.swiper.translate}px`;
        this.slider.classList.add('swiper--static');
      }

      trapFocus(this.modalContent, this.firstFocusableElement);

      if (document.body.style.overflow === 'hidden') this.isNested = true;

      document.body.style.overflow = 'hidden';

      return;
    }

    removeTrapFocus(this.btnMain);

    if (!this.isNested) document.body.style.overflow = '';

    if (!this.slider) return;

    const transitionendCallback = (e) => {
      if (
        e.target !== this.modalContent ||
        e.propertyName === 'visibility' ||
        this.isOpen
      )
        return;

      this.slider.classList.remove('swiper--static');
      this.slider.swiper.wrapperEl.style.left = '';

      this.modalContent.removeEventListener(
        'transitionend',
        transitionendCallback,
      );
    };

    this.modalContent.addEventListener('transitionend', transitionendCallback);
  }

  setA11yAttr() {
    this.btnsToggle.forEach((btnToggle) => {
      btnToggle.setAttribute('aria-expanded', false);
      btnToggle.setAttribute('aria-controls', this.modalContent.id);
      btnToggle.setAttribute('role', 'button');
    });
  }
}

customElements.define('modal-popup', ModalPopup);

class HeroPopup extends ModalPopup {
  constructor() {
    super();

    this.tabBtns = this.querySelector('.js-tabs-btns');
    this.tabsItems = this.querySelector('.js-tabs-items');
    this.compareBtns = this.querySelectorAll('.js-btn-compare');

    this.compareBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        this.detailsElement.classList.add('hero-banner__modal--compare');

        if (btn.classList.contains('js-btn-toggle')) return;

        this.modalContent.scrollTop = 0;
      });
    });

    this.tabBtns?.addEventListener('click', (e) => {
      const isTabBtn = e.target.classList.contains('js-btn-tab');

      if (!isTabBtn) return;

      const tabBtn = e.target;

      if (tabBtn.classList.contains('is-active')) return;

      const activeTabBtn = this.tabBtns.querySelector('.js-btn-tab.is-active');
      const newActiveTab = this.tabsItems.querySelector(
        `#${tabBtn.dataset.tabId}`,
      );
      const activeTab = this.tabsItems.querySelector('.js-tab.is-active');

      tabBtn.classList.add('is-active');
      activeTabBtn.classList.remove('is-active');
      activeTab.classList.remove('is-active');
      newActiveTab.classList.add('is-active');

      newActiveTab.scrollIntoView({
        behavior: 'smooth',
      });
    });
  }

  toggleModal() {
    super.toggleModal();

    header.classList.remove('is-hidden', this.isOpen);
    header.classList.toggle('menu-open', this.isOpen);
    header.classList.toggle('hide-nav', this.isOpen);
  }
}

customElements.define('hero-popup', HeroPopup);

class VideoPopup extends ModalPopup {
  constructor() {
    super();

    this.desktopVideo = this.querySelector('.js-desktop-video');

    if (this.desktopVideo.dataset.videoType === 'vimeo') {
      const iframe = this.desktopVideo;

      this.desktopVideo = new Vimeo.Player(iframe);
    }

    this.mobileVideo = this.querySelector('.js-mobile-video');

    if (this.mobileVideo?.dataset.videoType === 'vimeo') {
      const iframe = this.mobileVideo;

      this.mobileVideo = new Vimeo.Player(iframe);
    }

    this.setDevice();

    window.addEventListener('resize', () => {
      this.setDevice();

      if (!this.isOpen || !this.mobileVideo) return;

      this.isDesktop ? this.mobileVideo.pause() : this.desktopVideo.pause();
    });
  }

  toggleModal() {
    super.toggleModal();

    if (!this.desktopVideo) return;

    if (this.isOpen) {
      this.isDesktop || !this.mobileVideo
        ? this.desktopVideo.play()
        : this.mobileVideo.play();

      return;
    }

    this.desktopVideo.pause();
    this.mobileVideo?.pause();
  }

  setDevice() {
    this.isDesktop = matchMedia(`(min-width: ${mobileBreakpoint}px)`).matches;
  }
}
customElements.define('video-popup', VideoPopup);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(
        this.querySelector('template').content.firstElementChild.cloneNode(
          true,
        ),
      );

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(
        content.querySelector('.deferred-media__wrapper'),
      );
      if (focus) deferredElement.focus();
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

/**
 * Set cookie.
 * @param {String} name
 * @param {String} value
 * @param {Number} days
 * @returns Void
 */
const setCookie = (name, value, days) => {
  var expires = '';
  if (days) {
    var date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = '; expires=' + date.toUTCString();
  }
  document.cookie = name + '=' + (value || '') + expires + '; path=/';
};

/**
 * Get cookie data.
 * @param {String} name
 * @returns {String|Null}|
 */
const getCookie = (name) => {
  var nameEQ = name + '=';
  var ca = document.cookie.split(';');
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) == ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * Erase cookie value
 * @param {String} name
 */
const eraseCookie = (name) => {
  document.cookie = name + '=; Max-Age=-99999999';
};

class HeaderComponent extends HTMLElement {
  constructor() {
    super();

    this.header = this.querySelector('header');
    this.headerNavs = this.querySelectorAll('.js-nav');
    this.isTransparent = this.header.classList.contains('is-transparent');
    this.isSticky = this.header.classList.contains('is-sticky');
    this.scrollY = window.scrollY;
    this.headerDrawer = this.querySelector('header-drawer');

    this.headerNavs.forEach((headerNav) => {
      headerNav.addEventListener('click', (e) => {
        const isNavLink = e.target.classList.contains('js-nav-link');

        if (!(isNavLink && e.target.hash)) return;

        const targetEl = document.querySelector(e.target.hash);
        const isSamePath = window.location.pathname === e.target.pathname;

        if (targetEl || isSamePath) e.preventDefault();

        if (!targetEl) return;

        this.headerDrawer.closeMenuDrawer();
        targetEl.scrollIntoView({ behavior: 'smooth' });
      });
    });

    if (!this.isSticky) return;

    this.firstMainSection = document.querySelector('main .shopify-section');

    document.addEventListener('DOMContentLoaded', () => {
      this.setHeaderHeightProperty();
      this.setHeaderTransparent();

      window.addEventListener('resize', () => {
        this.setHeaderHeightProperty();
        this.setHeaderTransparent();
      });

      window.addEventListener('scroll', () => {
        this.setHeaderTransparent();
        this.setHeaderVisibility();
      });
    });

    window.addEventListener('shopify:section:load', (e) => {
      this.firstMainSection = document.querySelector('main .shopify-section');
    });
  }

  setHeaderHeightProperty() {
    document.documentElement.style.setProperty(
      '--header-height',
      `${this.header.offsetHeight}px`,
    );
  }

  setHeaderTransparent() {
    if (!this.firstMainSection) return;

    const sectionRect = this.firstMainSection.getBoundingClientRect();
    const headerOffsetTop =
      sectionRect.height + sectionRect.y - this.header.offsetHeight;

    this.header.classList.toggle('is-scrolled', headerOffsetTop <= 0);
  }

  setHeaderVisibility() {
    if (window.scrollY <= 0) {
      this.header.classList.remove('is-hidden');

      return;
    }

    this.header.classList.toggle('is-hidden', window.scrollY > this.scrollY);
    this.scrollY = window.scrollY;
  }
}

customElements.define('header-component', HeaderComponent);

// Tooltip plugin
var tooltip = new Drooltip({
  element: '.js-tooltip',
  animation: 'fade',
  background: '#000',
});

// Hover video
document.querySelectorAll('.js-hover-video').forEach((video) => {
  if (!matchMedia(`(min-width: ${mobileBreakpoint}px)`).matches) return;
  const videoEl = video.querySelector('video');
  if (!videoEl) return;

  video.addEventListener('mouseenter', () => {
    videoEl.volume = 0;
    videoEl.play();
  });
  video.addEventListener('mouseleave', () => videoEl.pause());
});
